/**
 * Was nur die App kann (Sitzung A3, Konzept `docs/strategie/app-konzept.md` §4.5): die reinen Entscheidungen der
 * Hülle, ohne `window`, damit die Tests sie ohne Browser prüfen. Wohin ein Link in der App führt, was die
 * Zurück-Taste unter Android tut, welche Farbe und Schrift die Statusleiste bekommt, wann Haptik spürbar sein darf und
 * wo ein Druckblatt als PDF umbricht. Mit der App spricht `schnittstelle.ts`.
 *
 * Diese Datei importiert nichts.
 */

// ─── Links ──────────────────────────────────────────────────────────────────

/** Endungen, die in der App als Datei kommen (herunterladen und öffnen) statt als Seite. */
const DATEI_ENDUNGEN = new Set([
  "pdf", "mp3", "m4a", "wav", "ogg", "aac", "mp4", "mov", "m4v", "webm", "zip", "doc", "docx", "xls", "xlsx", "ppt",
  "pptx", "odt", "ods", "odp", "rtf", "txt", "csv", "epub", "png", "jpg", "jpeg", "gif", "webp", "svg", "heic",
]);

/** Dateiart nach der Adresse: `pdf`, eine andere Datei oder `null` (eine Seite). Stripe-Belege enden auf `/pdf`. */
export function dateiArt(pfad: string): "pdf" | "datei" | null {
  const ohne = pfad.split(/[?#]/)[0].toLowerCase();
  if (ohne.endsWith("/pdf")) return "pdf";
  const letzter = ohne.split("/").pop() ?? "";
  const punkt = letzter.lastIndexOf(".");
  if (punkt < 1) return null;
  const endung = letzter.slice(punkt + 1);
  if (endung === "pdf") return "pdf";
  return DATEI_ENDUNGEN.has(endung) ? "datei" : null;
}

export type LinkZiel =
  /** Nichts tun: der Router oder die App selbst (Mail, Telefon) übernimmt. */
  | { art: "lassen" }
  /** Eine Seite der Plattform, die im Browser einen neuen Tab öffnen würde: in der App an Ort und Stelle. */
  | { art: "innen"; pfad: string }
  /** Ein PDF: in der App anzeigen bzw. herunterladen und öffnen. */
  | { art: "pdf"; url: string }
  /** Eine andere Datei: herunterladen und öffnen. */
  | { art: "datei"; url: string }
  /** Schon im Speicher des Browsers (`blob:`, `data:`): als Datei an die App geben. */
  | { art: "speicher"; url: string }
  /** Eine fremde Seite (Zoom, Google, Canva …): im Browser des Geräts. */
  | { art: "browser"; url: string };

/**
 * Wohin ein angetippter Link in der App führt. `herkunft` ist `window.location.origin`, `download` der Wert des
 * Attributs (`null` ohne), `neuesFenster` steht für `target="_blank"`. Im Browser wird nie gefragt.
 */
export function linkZiel(href: string, o: { herkunft: string; download: string | null; neuesFenster: boolean }): LinkZiel {
  let url: URL;
  try {
    url = new URL(href, o.herkunft);
  } catch {
    return { art: "lassen" };
  }
  if (url.protocol === "blob:" || url.protocol === "data:") return { art: "speicher", url: url.href };
  if (url.protocol !== "http:" && url.protocol !== "https:") return { art: "lassen" };
  const datei = dateiArt(url.pathname) ?? (o.download !== null ? "datei" : null);
  if (datei) return { art: datei, url: url.href };
  if (url.origin === o.herkunft) {
    return o.neuesFenster ? { art: "innen", pfad: `${url.pathname}${url.search}${url.hash}` } : { art: "lassen" };
  }
  return { art: "browser", url: url.href };
}

/** Dateiname für einen Download: das Attribut `download`, sonst der letzte Teil der Adresse, sonst „Datei“. */
export function dateinameAus(href: string, download: string | null): string {
  if (download && download.trim()) return download.trim();
  try {
    const letzter = decodeURIComponent(new URL(href).pathname.split("/").pop() ?? "");
    if (letzter && letzter !== "pdf") return letzter;
  } catch {
    // keine gültige Adresse
  }
  return dateiArt(href) === "pdf" ? "Dokument.pdf" : "Datei";
}

/** Grober MIME-Typ für den Download unter Android (die App braucht ihn zum Öffnen). */
export function mimeTypFuer(dateiname: string): string {
  const endung = dateiname.toLowerCase().split(".").pop() ?? "";
  const typen: Record<string, string> = {
    pdf: "application/pdf",
    mp3: "audio/mpeg",
    m4a: "audio/mp4",
    wav: "audio/wav",
    mp4: "video/mp4",
    zip: "application/zip",
    doc: "application/msword",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    txt: "text/plain",
    ics: "text/calendar",
  };
  return typen[endung] ?? "application/octet-stream";
}

/**
 * iOS kennt keinen Aufruf „im Browser öffnen“; Web to Native öffnet eine Adresse mit `loadIn=defaultBrowser` im
 * Browser des Geräts (Doku „Load In External Browser“, gilt für Android und iOS). Anker bleiben am Ende.
 */
export function mitBrowserMarke(href: string): string {
  try {
    const url = new URL(href);
    url.searchParams.set("loadIn", "defaultBrowser");
    return url.href;
  } catch {
    return href;
  }
}

// ─── Statusleiste ───────────────────────────────────────────────────────────

/**
 * Deckende Farbe aus `getComputedStyle(…).backgroundColor` als `#RRGGBB`; durchsichtige (unter 80 % Deckkraft) zählen
 * nicht, dann gilt die Farbe dahinter.
 */
export function deckendeFarbe(css: string): string | null {
  const m = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:[\s,/]+([\d.]+%?))?\s*\)$/i.exec(css.trim());
  if (!m) return null;
  const roh = m[4];
  const deckkraft = roh === undefined ? 1 : roh.endsWith("%") ? Number(roh.slice(0, -1)) / 100 : Number(roh);
  if (!(deckkraft >= 0.8)) return null;
  const hex = [m[1], m[2], m[3]].map((z) => Math.max(0, Math.min(255, Math.round(Number(z)))).toString(16).padStart(2, "0"));
  return `#${hex.join("").toUpperCase()}`;
}

/** Dunkle Schrift auf hellem Grund, helle auf dunklem (Web to Native: `style: "dark"` heißt dunkle Symbole). */
export function stilFuerFarbe(hex: string): "dark" | "light" {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!m) return "dark";
  const [r, g, b] = [m[1], m[2], m[3]].map((h) => {
    const k = parseInt(h, 16) / 255;
    return k <= 0.03928 ? k / 12.92 : ((k + 0.055) / 1.055) ** 2.4;
  });
  const helligkeit = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return helligkeit > 0.4 ? "dark" : "light";
}

// ─── Zurück-Taste (Android) ─────────────────────────────────────────────────

export type ZurueckSchritt =
  /** Ein Fenster, Blatt oder Menü ist offen: schließen wie mit Escape. */
  | "fenster"
  /** Ein Video läuft im Vollbild: Vollbild beenden. */
  | "vollbild"
  /** Auf der Übersicht: die App verlassen. */
  | "verlassen"
  /** Einen Schritt im Verlauf zurück. */
  | "zurueck"
  /** Ohne Verlauf (Seite direkt geöffnet): zur nächsthöheren Seite. */
  | "eltern";

/**
 * Was die Zurück-Taste unter Android tut: erst ein offenes Fenster oder Blatt schließen, dann einen Schritt zurück,
 * auf der Übersicht die App verlassen. `schritte` ist `history.state.idx` (React Router zählt die Schritte dieser
 * Sitzung), `uebersicht` die Startseite der Hülle (`/mein-bereich`).
 */
export function zurueckEntscheiden(lage: {
  fensterOffen: boolean;
  vollbild: boolean;
  pfad: string;
  uebersicht: string;
  schritte: number;
}): ZurueckSchritt {
  if (lage.fensterOffen) return "fenster";
  if (lage.vollbild) return "vollbild";
  const ohneSchraegstrich = (p: string) => (p.length > 1 ? p.replace(/\/+$/, "") : p);
  if (ohneSchraegstrich(lage.pfad) === ohneSchraegstrich(lage.uebersicht)) return "verlassen";
  return lage.schritte > 0 ? "zurueck" : "eltern";
}

// ─── Haptik ─────────────────────────────────────────────────────────────────

/** Kurz und selten: höchstens ein Antippen je 0,8 Sekunden, auch wenn mehrere Stellen gleichzeitig melden. */
export const HAPTIK_PAUSE_MS = 800;

export type HaptikArt = "tipp" | "erfolg";

/** Effekt von Web to Native (Doku „Haptic Feedback“): Herz leicht, Erledigt und Gespeichert als Erfolg. */
export function haptikEffekt(art: HaptikArt): "impactLight" | "notificationSuccess" {
  return art === "erfolg" ? "notificationSuccess" : "impactLight";
}

/** Darf jetzt eine Haptik kommen? `zuletzt` ist der Zeitpunkt der letzten (in Millisekunden). */
export function haptikErlaubt(jetzt: number, zuletzt: number | null): boolean {
  return zuletzt === null || jetzt - zuletzt >= HAPTIK_PAUSE_MS;
}

// ─── Bewertung ──────────────────────────────────────────────────────────────

/** Nach dem wievielten guten Moment (gespeichertes Angebot) die App um eine Bewertung bittet, und nur einmal. */
export const BEWERTUNG_NACH = 3;

export interface BewertungStand {
  zahl: number;
  gefragt: boolean;
}

/** Ein guter Moment mehr: neuer Stand und ob jetzt gefragt wird (beim dritten, danach nie wieder). */
export function bewertungNachMoment(vorher: Partial<BewertungStand> | null): { stand: BewertungStand; fragen: boolean } {
  const gefragt = vorher?.gefragt === true;
  const zahl = Math.max(0, Math.floor(Number(vorher?.zahl) || 0)) + 1;
  if (gefragt) return { stand: { zahl, gefragt: true }, fragen: false };
  const fragen = zahl >= BEWERTUNG_NACH;
  return { stand: { zahl, gefragt: fragen }, fragen };
}

// ─── Druckblatt als PDF ─────────────────────────────────────────────────────

/**
 * Wo ein Druckblatt beim Umwandeln in ein PDF umbricht (in Pixeln von oben). Wie beim Drucken im Browser: nie mitten
 * in einem Abschnitt mit `break-inside: avoid` (`verboten`), vor `break-before: page` immer (`erzwungen`), sonst an
 * der letzten Kante eines Blocks (`kandidaten`), die noch auf die Seite passt. Passt ein Abschnitt allein nicht auf
 * eine Seite, wird er geschnitten. Ergebnis: Anfang jeder Seite und am Ende die Gesamthöhe.
 */
export function seitenUmbrueche(o: {
  hoehe: number;
  seitenHoehe: number;
  verboten: readonly (readonly [number, number])[];
  kandidaten: readonly number[];
  erzwungen?: readonly number[];
}): number[] {
  const hoehe = Math.max(0, Math.ceil(o.hoehe));
  const seite = Math.max(1, Math.floor(o.seitenHoehe));
  const erlaubt = (y: number) => !o.verboten.some(([oben, unten]) => oben < y - 0.5 && y + 0.5 < unten);
  const zwang = [...new Set((o.erzwungen ?? []).map(Math.round))].filter((y) => y > 0 && y < hoehe).sort((a, b) => a - b);
  const stellen = [...new Set(o.kandidaten.map(Math.round))].filter((y) => y > 0 && y < hoehe && erlaubt(y)).sort((a, b) => a - b);
  const schnitte = [0];
  let start = 0;
  while (start < hoehe) {
    const grenze = start + seite;
    const muss = zwang.find((y) => y > start + 1 && y <= Math.min(grenze, hoehe));
    if (muss !== undefined && muss < hoehe) {
      schnitte.push(muss);
      start = muss;
      continue;
    }
    if (grenze >= hoehe) break;
    const passend = stellen.filter((y) => y > start + 1 && y <= grenze);
    const naechster = passend.length > 0 ? passend[passend.length - 1] : grenze;
    schnitte.push(naechster);
    start = naechster;
  }
  schnitte.push(hoehe);
  return schnitte;
}
